import {
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  ExclamationTriangleIcon,
  MegaphoneIcon,
} from '@heroicons/react/20/solid';
import { memo } from 'react';
import { twMerge } from 'tailwind-merge';

// Admin/staff-only badge mirroring the admin Messages table's "Notify" column:
// it surfaces the notification type a sent message went out as, inline in the
// Release Center feed, so an admin can confirm at a glance that a warning/update
// actually notified without leaving the release view (#1513). No-alert messages
// render no badge (the absence is the signal). Keeps the labels/tones aligned
// with the compose picker (MessageSettings NOTIFY_OPTIONS) admins already know.
const NOTIFY_META: Record<
  string,
  { label: string; Icon: typeof MegaphoneIcon; className: string }
> = {
  releaseWarning: {
    label: 'Release Warning',
    Icon: ExclamationTriangleIcon,
    className:
      'bg-amber-50 text-amber-700 ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-400/20',
  },
  update: {
    label: 'Update',
    Icon: ArrowPathIcon,
    className:
      'bg-sky-50 text-sky-700 ring-sky-600/20 dark:bg-sky-500/10 dark:text-sky-400 dark:ring-sky-400/20',
  },
  marketing: {
    label: 'Marketing',
    Icon: MegaphoneIcon,
    className:
      'bg-violet-50 text-violet-700 ring-violet-600/20 dark:bg-violet-500/10 dark:text-violet-400 dark:ring-violet-400/20',
  },
  discordWarning: {
    label: 'Discord Warning',
    Icon: ChatBubbleLeftRightIcon,
    className:
      'bg-indigo-50 text-indigo-700 ring-indigo-600/20 dark:bg-indigo-500/10 dark:text-indigo-400 dark:ring-indigo-400/20',
  },
};

// Fallback tone for an unknown, non-empty notify value (shown verbatim).
const UNKNOWN_CLASSNAME =
  'bg-zinc-100 text-zinc-500 ring-zinc-500/20 dark:bg-zinc-500/10 dark:text-zinc-400 dark:ring-zinc-400/20';

const NotifyBadge = ({ notify }: { notify?: string | null }) => {
  // No-alert messages (notify nullable / '0' / '') render no badge at all — the
  // absence is the signal, and a badge on every message would be noise.
  if (!notify || notify === '0') {
    return null;
  }

  const meta = NOTIFY_META[notify] ?? null;

  // Unknown, non-empty notify value: show it verbatim so nothing is silently
  // hidden (matches the admin table's fallback).
  const label = meta ? meta.label : notify;
  const Icon = meta?.Icon;
  const className = meta?.className ?? UNKNOWN_CLASSNAME;

  return (
    <span
      className={twMerge(
        'inline-flex shrink-0 select-none items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 font-medium text-xs ring-1 ring-inset',
        className,
      )}
      title={`Sent as: ${label}`}
    >
      {Icon ? <Icon className="size-3 shrink-0" aria-hidden="true" /> : null}
      {label}
    </span>
  );
};

export default memo(NotifyBadge, (prev, next) => prev.notify === next.notify);
